import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { 
  obtenerArticulos, 
  crearArticulo, 
  actualizarArticulo, 
  eliminarArticulo 
} from '../../services/articuloService';
import AdminLayout from '../../components/layout/AdminLayout';
import Modal from '../../components/common/Modal';
import NotificationModal from '../../components/common/NotificationModal';
import ConfirmActionModal from '../../components/common/ConfirmActionModal';
import BackButton from '../../components/common/BackButton';
import './AdminArticulos.css';

const AdminArticulos = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [articulos, setArticulos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [formData, setFormData] = useState({
    titulo: '',
    resumen: '',
    contenido: '',
    categoria: 'Ley 1480'
  });

  // Estados para notificaciones
  const [notification, setNotification] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'success'
  });

  // Estado para confirmacion de eliminacion
  const [confirmDelete, setConfirmDelete] = useState({
    isOpen: false,
    id: null,
    titulo: ''
  });

  const categorias = ['Ley 1480', 'Traspasos', 'RUNT', 'SIMIT', 'Embargos', 'SOAT', 'TecnoMecanica', 'Otros'];

  const showNotification = (title, message, type = 'success') => {
    setNotification({
      isOpen: true,
      title,
      message,
      type
    });
  };

  const closeNotification = () => {
    setNotification({ ...notification, isOpen: false });
  };

  const cargarArticulos = useCallback(async () => {
    try {
      setLoading(true);
      const data = await obtenerArticulos();
      setArticulos(data);
    } catch (error) {
      console.error('Error:', error);
      showNotification('Error', 'Error al cargar articulos: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    cargarArticulos();
  }, [user, navigate, cargarArticulos]);

  const handleOpenModal = (articulo = null) => {
    if (articulo) {
      setEditando(articulo);
      setFormData({
        titulo: articulo.titulo,
        resumen: articulo.resumen,
        contenido: articulo.contenido,
        categoria: articulo.categoria
      });
    } else {
      setEditando(null);
      setFormData({
        titulo: '',
        resumen: '',
        contenido: '',
        categoria: 'Ley 1480'
      });
    }
    setModalOpen(true);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editando) {
        await actualizarArticulo(editando.id, formData);
        showNotification('Articulo Actualizado', 'El articulo ha sido actualizado exitosamente', 'success');
      } else {
        await crearArticulo(formData);
        showNotification('Articulo Creado', 'El articulo ha sido creado exitosamente', 'success');
      }
      setModalOpen(false);
      cargarArticulos();
    } catch (error) {
      showNotification('Error', 'Error al guardar: ' + error.message, 'error');
    }
  };

  const handleEliminarClick = (id, titulo) => {
    setConfirmDelete({
      isOpen: true,
      id: id,
      titulo: titulo
    });
  };

  const handleConfirmDelete = async () => {
    try {
      await eliminarArticulo(confirmDelete.id);
      showNotification('Articulo Eliminado', `El articulo "${confirmDelete.titulo}" ha sido eliminado exitosamente`, 'success');
      setConfirmDelete({ isOpen: false, id: null, titulo: '' });
      cargarArticulos();
    } catch (error) {
      showNotification('Error', 'Error al eliminar: ' + error.message, 'error');
      setConfirmDelete({ isOpen: false, id: null, titulo: '' });
    }
  };

  const handleCancelDelete = () => {
    setConfirmDelete({ isOpen: false, id: null, titulo: '' });
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return 'Fecha no disponible';
    return fecha?.toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) return <div className="loading">Cargando articulos...</div>;

  return (
    <AdminLayout>
      <div className="admin-articulos">
        <div className="admin-navigation">
          <BackButton to="/dashboard" label="Volver al Dashboard" variant="outline" />
        </div>

        <div className="admin-header">
          <h1>Administracion de Articulos</h1>
          <button className="btn-nuevo" onClick={() => handleOpenModal()}>
            + Nuevo Articulo
          </button>
        </div>

        <div className="articulos-lista">
          {articulos.length === 0 ? (
            <p className="no-articulos">No hay articulos creados</p>
          ) : (
            <table className="articulos-tabla">
              <thead>
                <tr>
                  <th>Titulo</th>
                  <th>Categoria</th>
                  <th>Fecha</th>
                  <th>Vistas</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {articulos.map(articulo => (
                  <tr key={articulo.id}>
                    <td className="titulo-cell">{articulo.titulo}</td>
                    <td><span className="categoria-badge">{articulo.categoria}</span></td>
                    <td>{formatearFecha(articulo.fechaPublicacion)}</td>
                    <td>{articulo.vistas || 0}</td>
                    <td className="acciones-cell">
                      <button 
                        className="btn-editar"
                        onClick={() => handleOpenModal(articulo)}
                      >
                        Editar
                      </button>
                      <button 
                        className="btn-eliminar"
                        onClick={() => handleEliminarClick(articulo.id, articulo.titulo)}
                      >
                        Eliminar
                      </button>
                      <a 
                        href={`/blog/${articulo.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-ver"
                      >
                        Ver
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Modal para crear/editar articulo */}
        <Modal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          title={editando ? 'Editar Articulo' : 'Nuevo Articulo'}
        >
          <form onSubmit={handleSubmit} className="articulo-form">
            <div className="form-group">
              <label>Titulo *</label>
              <input
                type="text"
                name="titulo"
                value={formData.titulo}
                onChange={handleChange}
                required
                placeholder="Titulo del articulo"
              />
            </div>

            <div className="form-group">
              <label>Categoria *</label>
              <select
                name="categoria"
                value={formData.categoria}
                onChange={handleChange}
                required
              >
                {categorias.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Resumen *</label>
              <textarea
                name="resumen"
                value={formData.resumen}
                onChange={handleChange}
                required
                rows="3"
                placeholder="Breve resumen del articulo (aparecera en la lista)"
              />
            </div>

            <div className="form-group">
              <label>Contenido *</label>
              <textarea
                name="contenido"
                value={formData.contenido}
                onChange={handleChange}
                required
                rows="10"
                placeholder="Contenido completo del articulo..."
              />
            </div>

            <div className="form-actions">
              <button type="button" className="btn-cancelar" onClick={() => setModalOpen(false)}>
                Cancelar
              </button>
              <button type="submit" className="btn-guardar">
                {editando ? 'Actualizar' : 'Publicar'} Articulo
              </button>
            </div>
          </form>
        </Modal>

        {/* Notificacion */}
        <NotificationModal
          isOpen={notification.isOpen}
          onClose={closeNotification}
          title={notification.title}
          message={notification.message}
          type={notification.type}
          autoClose={true}
        />

        {/* Modal de confirmacion para eliminar */}
        <ConfirmActionModal
          isOpen={confirmDelete.isOpen}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          title="Confirmar eliminacion"
          message={`¿Estas seguro de que deseas eliminar el articulo "${confirmDelete.titulo}"? Esta accion no se puede deshacer.`}
          confirmText="Si, eliminar"
          cancelText="Cancelar"
        />
      </div>
    </AdminLayout>
  );
};

export default AdminArticulos;